import React, { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import {
  createATR,
  updateATR,
  listPOs,
  listPSOs,
  listTeachersForPicker,
  suggestATRDraft,
} from "../../../../services/program.service";

// Phase 6a — Create or edit an Action Taken Report.
//
// Props:
//   programId       — required
//   atr             — existing ATR doc for edit; null for create
//   prefill         — { outcomeType, outcomeId, observedAttainment,
//                        targetAttainment, observation } from the "Draft ATR"
//                     button on AttainmentTab. Sets initial form values.
//   onClose(reload) — call with true if the parent should refresh

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5"
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const AtrForm = ({ programId, atr, prefill, onClose }) => {
  const isEdit = !!atr;
  const initial = atr || prefill || {};

  const [form, setForm] = useState({
    outcomeType: initial.outcomeType || "PO",
    outcomeId: initial.outcomeId || "",
    batch: initial.batch ?? "",
    observedAttainment: initial.observedAttainment ?? "",
    targetAttainment: initial.targetAttainment ?? "",
    observation: initial.observation || "",
    rootCause: initial.rootCause || "",
    plannedAction: initial.plannedAction || "",
    ownerTeacher: initial.ownerTeacher?._id || initial.ownerTeacher || "",
    dueDate: initial.dueDate ? initial.dueDate.slice(0, 10) : "",
    status: initial.status || "open",
  });
  const [pos, setPos] = useState([]);
  const [psos, setPsos] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loadingPickers, setLoadingPickers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiWasMock, setAiWasMock] = useState(false);
  // _aiContext is passed in via prefill from AttainmentTab's "Draft ATR" button
  const aiContext = prefill?._aiContext || null;

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, submitting]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pData, psData, tData] = await Promise.all([
          listPOs(programId),
          listPSOs(programId),
          listTeachersForPicker(),
        ]);
        if (cancelled) return;
        setPos(pData.pos || []);
        setPsos(psData.psos || []);
        setTeachers(tData.teachers || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              "Failed to load outcomes/teachers"
          );
        }
      } finally {
        if (!cancelled) setLoadingPickers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [programId]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleAIDraft = async () => {
    if (!aiContext) return;
    setAiDrafting(true);
    setAiWasMock(false);
    try {
      const draft = await suggestATRDraft(aiContext);
      setField("observation", draft.observation || "");
      setField("rootCause", draft.root_cause || "");
      setField("plannedAction", draft.planned_action || "");
      if (draft._mock) {
        setAiWasMock(true);
        toast("AI draft generated (demo data — Python AI offline)", { icon: "🧪" });
      } else {
        toast.success("AI draft filled in — review and edit before saving");
      }
    } catch {
      toast.error("AI draft failed — fill in manually");
    } finally {
      setAiDrafting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.outcomeType || !form.outcomeId) {
      setError("Please pick the PO/PSO this ATR addresses.");
      return;
    }
    if (!form.observation.trim()) {
      setError("Observation is required.");
      return;
    }
    if (!form.plannedAction.trim()) {
      setError("Planned action is required.");
      return;
    }

    const payload = {
      outcomeType: form.outcomeType,
      outcomeId: form.outcomeId,
      batch: form.batch === "" ? null : Number(form.batch),
      observedAttainment:
        form.observedAttainment === ""
          ? null
          : Number(form.observedAttainment),
      targetAttainment:
        form.targetAttainment === ""
          ? null
          : Number(form.targetAttainment),
      observation: form.observation.trim(),
      rootCause: form.rootCause.trim(),
      plannedAction: form.plannedAction.trim(),
      ownerTeacher: form.ownerTeacher || null,
      dueDate: form.dueDate || null,
      status: form.status,
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateATR(atr._id, payload);
        toast.success("ATR updated");
      } else {
        await createATR(programId, payload);
        toast.success("ATR created");
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const outcomesForType = form.outcomeType === "PO" ? pos : psos;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => !submitting && onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>
                {isEdit ? "Edit ATR" : "New ATR"}
              </div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                Action Taken Report
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 max-w-xl">
                NBA Criterion 8 evidence — document observation, root cause,
                and planned action for a PO/PSO where attainment fell short
                of target. Close it later with closure notes once the
                intervention is complete.
              </p>
            </div>
            <button
              onClick={() => onClose(false)}
              disabled={submitting}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-5 overflow-y-auto"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Outcome Type *">
                <select
                  value={form.outcomeType}
                  onChange={(e) => {
                    setField("outcomeType", e.target.value);
                    setField("outcomeId", "");
                  }}
                  disabled={isEdit}
                  className={inputBase}
                >
                  <option value="PO">PO (Program Outcome)</option>
                  <option value="PSO">PSO (Program Specific Outcome)</option>
                </select>
              </Field>
              <Field label="Outcome *" hint={loadingPickers ? "Loading…" : `${outcomesForType.length} options`}>
                <select
                  value={form.outcomeId}
                  onChange={(e) => setField("outcomeId", e.target.value)}
                  required
                  disabled={isEdit || loadingPickers}
                  className={inputBase}
                >
                  <option value="">Select…</option>
                  {outcomesForType.map((o) => (
                    <option key={o._id} value={o._id}>
                      {o.code}
                      {o.category ? ` — ${o.category}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Observed Attainment" hint="0–3 level (optional)">
                <input
                  type="number"
                  min={0}
                  max={3}
                  step={0.01}
                  value={form.observedAttainment}
                  onChange={(e) => setField("observedAttainment", e.target.value)}
                  className={inputBase}
                />
              </Field>
              <Field label="Target Attainment" hint="0–3 level (optional)">
                <input
                  type="number"
                  min={0}
                  max={3}
                  step={0.01}
                  value={form.targetAttainment}
                  onChange={(e) => setField("targetAttainment", e.target.value)}
                  className={inputBase}
                />
              </Field>
              <Field label="Batch" hint="e.g. 2024 (optional)">
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={form.batch}
                  onChange={(e) => setField("batch", e.target.value)}
                  className={inputBase}
                />
              </Field>
            </div>

            {/* AI Draft button — only visible when context is available (from "Draft ATR" on dashboard) */}
            {aiContext && (
              <div className="flex items-center justify-between py-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  AI can pre-fill the three fields below based on attainment data.
                  {aiWasMock && (
                    <span className="ml-1 text-amber-600 dark:text-amber-400 font-medium">🧪 Using demo data.</span>
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleAIDraft}
                  disabled={aiDrafting || submitting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-accent1 text-white text-xs font-semibold hover:bg-accent1/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {aiDrafting ? "Drafting…" : "✨ AI Draft"}
                </button>
              </div>
            )}

            <Field label="Observation *" hint="What did the data show? Cite the numbers.">
              <textarea
                value={form.observation}
                onChange={(e) => setField("observation", e.target.value)}
                rows={3}
                required
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            <Field label="Root Cause" hint="Optional — your analysis of WHY">
              <textarea
                value={form.rootCause}
                onChange={(e) => setField("rootCause", e.target.value)}
                rows={2}
                placeholder="Lack of design exposure in lab sessions; questions skewed toward recall…"
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            <Field label="Planned Action *" hint="The intervention you'll take">
              <textarea
                value={form.plannedAction}
                onChange={(e) => setField("plannedAction", e.target.value)}
                rows={3}
                required
                placeholder="Revise lab manual to include 3 open-ended design tasks; train TAs on rubric…"
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Owner Teacher" hint={loadingPickers ? "Loading…" : `${teachers.length} options`}>
                <select
                  value={form.ownerTeacher}
                  onChange={(e) => setField("ownerTeacher", e.target.value)}
                  disabled={loadingPickers}
                  className={inputBase}
                >
                  <option value="">(unassigned)</option>
                  {teachers.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                      {t.department ? ` · ${t.department}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Due Date">
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setField("dueDate", e.target.value)}
                  className={inputBase}
                />
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                  className={inputBase}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  {isEdit && <option value="cancelled">Cancelled</option>}
                </select>
              </Field>
            </div>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={submitting}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Create ATR"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AtrForm;
